import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { blogPosts } from '../mock';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const categories = ['All', 'SEO Strategy', 'Lead Generation', 'Content Strategy', 'Analytics', 'CRO'];

  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="pt-32 pb-12 px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-amber-100 text-amber-700 mb-4">Blog</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Insights on B2B SaaS Growth
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Practical strategies for turning SEO content into qualified leads.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-12 px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Article</h2>
            <Card className="border-2 hover:shadow-xl transition-shadow overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image Placeholder */}
                <div className="bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center p-12">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-amber-600 mb-2">{featuredPost.category}</div>
                    <div className="text-amber-700 font-medium">Featured Post</div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col justify-center">
                  <Badge className="bg-amber-100 text-amber-700 w-fit mb-4">
                    {featuredPost.category}
                  </Badge>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {featuredPost.title}
                  </h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
                    <div className="flex items-center space-x-1">
                      <Calendar size={16} />
                      <span>{new Date(featuredPost.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock size={16} />
                      <span>{featuredPost.readTime}</span>
                    </div>
                  </div>
                  <Link to={`/blog/${featuredPost.id}`}>
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white w-fit">
                      Read Article <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 px-6 lg:px-8 bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'border-gray-300 hover:border-amber-600 hover:text-amber-600'
                }
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Latest Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card key={post.id} className="border-2 hover:shadow-lg transition-all hover:border-amber-200 flex flex-col">
                {/* Image Placeholder */}
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 h-48 flex items-center justify-center">
                  <div className="text-center px-4">
                    <div className="text-3xl font-bold text-gray-400">{post.category}</div>
                  </div>
                </div>

                <CardHeader>
                  <Badge className="bg-amber-100 text-amber-700 w-fit mb-2">
                    {post.category}
                  </Badge>
                  <CardTitle className="text-xl line-clamp-2">{post.title}</CardTitle>
                  <CardDescription className="line-clamp-3">{post.excerpt}</CardDescription>
                </CardHeader>

                <CardContent className="mt-auto">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <Link to={`/blog/${post.id}`}>
                    <Button variant="outline" className="w-full border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white">
                      Read More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Want content strategies tailored to your SaaS?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Let's discuss how to build a content engine that drives qualified leads.
          </p>
          <Link to="/">
            <Button
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg"
            >
              Get in Touch <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;